import React, { useState, createElement } from 'react';
import { StatsCard } from './shared/StatsCard';
import { Settings, Brain, Repeat, Zap, Globe, Theater, AlertTriangle, Info, TrendingUp, DollarSign, Droplet, Fish, Wallet, AlertCircle, BarChart, Sliders, Download, Bot, User } from 'lucide-react';
// Fallback methods
const fallbackMethods = [{
  id: 'lookalike',
  label: 'Lookalike Audiences',
  description: 'Find similar users based on known whales/dolphins',
  icon: <Repeat size={16} className="text-indigo-500" data-id="element-13609" />,
  defaultThreshold: 70,
  currentThreshold: 70,
  effectiveness: 85,
  revenue: 420000,
  aiRevenue: 126000,
  humanRevenue: 294000
}, {
  id: 'broad',
  label: 'Broad Targeting',
  description: 'Run ads/offers to wide pool until algorithm gathers signals',
  icon: <Zap size={16} className="text-amber-500" data-id="element-13610" />,
  defaultThreshold: 50,
  currentThreshold: 50,
  effectiveness: 65,
  revenue: 275000,
  aiRevenue: 192500,
  humanRevenue: 82500
}, {
  id: 'context',
  label: 'Context Fallback',
  description: 'Device, GEO, time-of-day, minimal demographics',
  icon: <Globe size={16} className="text-blue-500" data-id="element-13611" />,
  defaultThreshold: 40,
  currentThreshold: 40,
  effectiveness: 55,
  revenue: 180000,
  aiRevenue: 144000,
  humanRevenue: 36000
}, {
  id: 'persona',
  label: 'Persona/Emotion Fallback',
  description: 'Only used when no other signals are available',
  icon: <Theater size={16} className="text-pink-500" data-id="element-13612" />,
  defaultThreshold: 30,
  currentThreshold: 30,
  effectiveness: 40,
  revenue: 120000,
  aiRevenue: 96000,
  humanRevenue: 24000
}];
// User segments
const segments = [{
  id: 'whales',
  label: 'Whales',
  icon: <DollarSign size={16} className="text-emerald-500" data-id="element-13613" />,
  algorithmConfidence: 92,
  fallbackMethod: 'lookalike',
  retentionRisk: 'low',
  customerClass: 'VIP'
}, {
  id: 'dolphins',
  label: 'Dolphins',
  icon: <Droplet size={16} className="text-blue-500" data-id="element-13614" />,
  algorithmConfidence: 85,
  fallbackMethod: 'lookalike',
  retentionRisk: 'low',
  customerClass: 'Spender'
}, {
  id: 'minnows',
  label: 'Minnows',
  icon: <Fish size={16} className="text-cyan-500" data-id="element-13615" />,
  algorithmConfidence: 72,
  fallbackMethod: 'broad',
  retentionRisk: 'medium',
  customerClass: 'Low Spender'
}, {
  id: 'non-spenders',
  label: 'Non-Spenders',
  icon: <Wallet size={16} className="text-gray-500" data-id="element-13616" />,
  algorithmConfidence: 45,
  fallbackMethod: 'context',
  retentionRisk: 'high',
  customerClass: 'Newbie'
}, {
  id: 'churn-risks',
  label: 'Churn Risks',
  icon: <AlertCircle size={16} className="text-red-500" data-id="element-13617" />,
  algorithmConfidence: 88,
  fallbackMethod: 'persona',
  retentionRisk: 'critical',
  customerClass: 'Dormant'
}];
// AI coaching suggestions
const aiCoachingSuggestions = [{
  id: 'suggestion1',
  title: 'Increase algorithm confidence threshold for Whales',
  description: 'Whales have high algorithm coverage. Consider increasing the threshold to 95% to ensure more precise targeting.',
  impact: 'high',
  segment: 'whales'
}, {
  id: 'suggestion2',
  title: 'Switch Non-Spenders from Context to Broad targeting',
  description: 'Non-Spenders show better response to broad targeting in recent tests. Consider updating the fallback method.',
  impact: 'medium',
  segment: 'non-spenders'
}, {
  id: 'suggestion3',
  title: 'Reduce persona fallback usage for Churn Risks',
  description: 'Persona fallback is not performing well for Churn Risks. Consider switching to Lookalike targeting based on retained users.',
  impact: 'high',
  segment: 'churn-risks'
}];
// Risk alerts
const riskAlerts = [{
  id: 'risk1',
  title: 'High Fallback Usage for Non-Spenders',
  description: 'Non-Spenders have 55% fallback usage, which may lead to generic experiences and lower conversion.',
  severity: 'medium',
  segment: 'non-spenders'
}, {
  id: 'risk2',
  title: 'Persona Fallback Overuse Warning',
  description: 'Persona fallback has the lowest effectiveness (40%). Minimize usage to improve monetization.',
  severity: 'high',
  segment: 'all'
}, {
  id: 'risk3',
  title: 'Retention Risk for Minnows',
  description: 'Minnows segment shows increasing fallback usage, which correlates with declining retention rates.',
  severity: 'medium',
  segment: 'minnows'
}];
export const OwnerControlLayer: React.FC = () => {
  const [thresholds, setThresholds] = useState(fallbackMethods.reduce((acc, method) => ({
    ...acc,
    [method.id]: method.currentThreshold
  }), {}));
  const [fallbackPriority, setFallbackPriority] = useState(['lookalike', 'broad', 'context', 'persona']);
  const [segmentFallbacks, setSegmentFallbacks] = useState(segments.reduce((acc, segment) => ({
    ...acc,
    [segment.id]: segment.fallbackMethod
  }), {}));
  const [aiCommissionRates, setAiCommissionRates] = useState({
    free: 15,
    standard: 10,
    premium: 8.5
  });
  const [hybridRoutingThreshold, setHybridRoutingThreshold] = useState(120);
  const [showRevenueToggle, setShowRevenueToggle] = useState(true);
  const [exportSuccess, setExportSuccess] = useState(false);
  const handleThresholdChange = (methodId: string, value: number) => {
    setThresholds({
      ...thresholds,
      [methodId]: value
    });
  };
  const handleFallbackMethodChange = (segmentId: string, method: string) => {
    setSegmentFallbacks({
      ...segmentFallbacks,
      [segmentId]: method
    });
  };
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newPriority = [...fallbackPriority];
    const temp = newPriority[index - 1];
    newPriority[index - 1] = newPriority[index];
    newPriority[index] = temp;
    setFallbackPriority(newPriority);
  };
  const handleMoveDown = (index: number) => {
    if (index === fallbackPriority.length - 1) return;
    const newPriority = [...fallbackPriority];
    const temp = newPriority[index + 1];
    newPriority[index + 1] = newPriority[index];
    newPriority[index] = temp;
    setFallbackPriority(newPriority);
  };
  const handleCommissionRateChange = (tier: string, value: number) => {
    setAiCommissionRates({
      ...aiCommissionRates,
      [tier]: value
    });
  };
  const handleExportData = () => {
    // Prepare the data for export
    const exportData = [{
      'Configuration Type': 'Global Settings',
      Setting: 'Hybrid Routing Threshold',
      Value: `$${hybridRoutingThreshold}`,
      Description: 'Lifetime spend before routing to human operator'
    }, {
      'Configuration Type': 'Commission Rates',
      Setting: 'Free Tier Commission',
      Value: `${aiCommissionRates.free}%`,
      Description: 'Commission rate for free tier AI revenue'
    }, {
      'Configuration Type': 'Commission Rates',
      Setting: 'Standard Tier Commission',
      Value: `${aiCommissionRates.standard}%`,
      Description: 'Commission rate for standard tier AI revenue'
    }, {
      'Configuration Type': 'Commission Rates',
      Setting: 'Premium Tier Commission',
      Value: `${aiCommissionRates.premium}%`,
      Description: 'Commission rate for premium tier AI revenue'
    }, ...segments.map(segment => ({
      'Configuration Type': 'Segment Settings',
      Setting: `${segment.label} Fallback Method`,
      Value: segmentFallbacks[segment.id],
      Description: `Fallback method for ${segment.label} segment`
    })), ...fallbackMethods.map(method => ({
      'Configuration Type': 'Fallback Method',
      Setting: `${method.label} Threshold`,
      Value: `${thresholds[method.id]}%`,
      Description: `Confidence threshold for ${method.label}`
    }))];
    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [headers.join(','), ...exportData.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))].join('\n');
    // Create a blob and download link
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'monetization_settings.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Show success message
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };
  const totalRevenue = fallbackMethods.reduce((sum, method) => sum + method.revenue, 0);
  const totalAiRevenue = fallbackMethods.reduce((sum, method) => sum + method.aiRevenue, 0);
  const totalHumanRevenue = fallbackMethods.reduce((sum, method) => sum + method.humanRevenue, 0);
  const aiPercentage = Math.round(totalAiRevenue / totalRevenue * 100);
  const humanPercentage = 100 - aiPercentage;
  // Calculate AI commission
  const aiCommissionRate = aiCommissionRates.free / 100; // 15% commission rate for AI
  const aiCommission = totalAiRevenue * aiCommissionRate;
  const avgEffectiveness = fallbackMethods.reduce((sum, method) => sum + method.effectiveness, 0) / fallbackMethods.length;
  return <div data-id="element-13618">
      <div className="flex items-center justify-between mb-6" data-id="element-13619">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-13620">
          Owner Control Layer
        </h2>
        <div className="flex items-center gap-3" data-id="element-13621">
          <button onClick={handleExportData} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-13622">
            <Download size={16} data-id="element-13623" />
            <span data-id="element-13624">Export Settings</span>
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-13625">
            <Settings size={16} data-id="element-13626" />
            <span data-id="element-13627">Advanced Settings</span>
          </button>
          {exportSuccess && <div className="absolute top-4 right-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-md shadow-md" data-id="element-13628">
              Settings exported successfully!
            </div>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-13629">
        <StatsCard title="Algorithm Effectiveness" value={`${avgEffectiveness.toFixed(1)}%`} change={3.2} changeLabel="vs last month" icon={<Brain size={16} className="text-purple-500" data-id="element-13631" />} className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20" data-id="element-13630" />
        <StatsCard title="Fallback Revenue" value={`$${(totalRevenue / 1000).toFixed(1)}K`} change={7.5} changeLabel="vs last month" icon={<TrendingUp size={16} className="text-green-500" data-id="element-13633" />} className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-green-900/20" data-id="element-13632" />
        <StatsCard title="Active Fallbacks" value={fallbackMethods.length.toString()} change={0} changeLabel="no change" icon={<Sliders size={16} className="text-blue-500" data-id="element-13635" />} data-id="element-13634" />
        <StatsCard title="Risk Level" value="Medium" change={-5} changeLabel="improved" icon={<AlertTriangle size={16} className="text-amber-500" data-id="element-13637" />} className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20" data-id="element-13636" />
      </div>
      {/* Revenue Split and AI Commission Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-13638">
        <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20 p-4 rounded-lg shadow-sm" data-id="element-13639">
          <div className="flex justify-between items-start" data-id="element-13640">
            <div data-id="element-13641">
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center group relative" data-id="element-13642">
                Revenue Split (AI vs Human)
                <Info size={14} className="ml-1 text-gray-400 cursor-help" data-id="element-13643" />
                <span className="absolute left-0 top-full mt-1 w-64 p-2 bg-white dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-13644">
                  AI revenue is generated via automated chat, upsells, and
                  campaigns. Human revenue is generated by creators or
                  moderators.
                </span>
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-13645">
                {aiPercentage}% / {humanPercentage}%
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-13646">
                ${totalAiRevenue.toLocaleString()} / $
                {totalHumanRevenue.toLocaleString()}
              </p>
            </div>
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-13647">
              <Zap className="w-5 h-5 text-purple-500 dark:text-purple-400" data-id="element-13648" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-2" data-id="element-13649">
            <div className="flex items-center text-sm" data-id="element-13650">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-13651" />
              <span className="text-green-500 font-medium" data-id="element-13652">11.2%</span>
              <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-13653">
                AI growth
              </span>
            </div>
            <div className="flex items-center" data-id="element-13654">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-2" data-id="element-13655">
                Show:
              </span>
              <label className="relative inline-flex items-center cursor-pointer" data-id="element-13656">
                <input type="checkbox" checked={showRevenueToggle} onChange={() => setShowRevenueToggle(!showRevenueToggle)} className="sr-only peer" data-id="element-13657" />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" data-id="element-13658"></div>
              </label>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 p-4 rounded-lg shadow-sm" data-id="element-13659">
          <div className="flex justify-between items-start" data-id="element-13660">
            <div data-id="element-13661">
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-13662">
                AI Commission Fee
              </p>
              <h3 className="text-2xl font-bold mt-1" data-id="element-13663">
                ${aiCommission.toLocaleString()}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-13664">
                Free: {aiCommissionRates.free}%, $30:{' '}
                {aiCommissionRates.standard}%, $100: {aiCommissionRates.premium}
                %
              </p>
            </div>
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg" data-id="element-13665">
              <DollarSign className="w-5 h-5 text-indigo-500 dark:text-indigo-400" data-id="element-13666" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-sm" data-id="element-13667">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" data-id="element-13668" />
            <span className="text-green-500 font-medium" data-id="element-13669">9.5%</span>
            <span className="text-gray-500 dark:text-gray-400 ml-1" data-id="element-13670">
              vs last month
            </span>
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800 p-4" data-id="element-13671">
          <div className="flex items-start" data-id="element-13672">
            <Zap className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-13673" />
            <div data-id="element-13674">
              <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-13675">
                Hybrid Routing Settings
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-13676">
                AI handles all new and low-value customers. After $
                {hybridRoutingThreshold} lifetime spend, customer is routed to
                Human operator for personalized attention.
              </p>
              <div className="mt-2 flex items-center" data-id="element-13677">
                <span className="text-xs text-gray-600 dark:text-gray-400 mr-2" data-id="element-13678">
                  Threshold: ${hybridRoutingThreshold}
                </span>
                <input type="range" min="50" max="500" step="10" value={hybridRoutingThreshold} onChange={e => setHybridRoutingThreshold(parseInt(e.target.value))} className="w-full h-2 bg-blue-200 dark:bg-blue-700 rounded-lg appearance-none cursor-pointer" data-id="element-13679" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-13680">
        <div className="lg:col-span-2" data-id="element-13681">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-13682">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13683">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13684">
                <Sliders size={18} className="text-blue-500 mr-2" data-id="element-13685" />
                Algorithm Confidence Thresholds
              </h3>
            </div>
            <div className="p-6" data-id="element-13686">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6" data-id="element-13687">
                Set the minimum confidence level required for algorithmic
                targeting. When confidence falls below threshold, the system
                will use the corresponding fallback method.
              </p>
              <div className="space-y-6" data-id="element-13688">
                {fallbackMethods.map(method => <div key={method.id} className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4" data-id="element-13689">
                    <div className="flex items-center justify-between mb-2" data-id="element-13690">
                      <div className="flex items-center" data-id="element-13691">
                        <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-800 flex items-center justify-center mr-3" data-id="element-13692">
                          {method.icon}
                        </div>
                        <div data-id="element-13693">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13694">
                            {method.label}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-13695">
                            {method.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-13696">
                        Effectiveness:{' '}
                        <span className="font-medium" data-id="element-13697">
                          {method.effectiveness}%
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center mt-3" data-id="element-13698">
                      <div className="w-full" data-id="element-13699">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-13700">
                          Confidence Threshold: {thresholds[method.id]}%
                        </label>
                        <div className="flex items-center" data-id="element-13701">
                          <input type="range" min="0" max="100" value={thresholds[method.id]} onChange={e => handleThresholdChange(method.id, parseInt(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-13702" />
                        </div>
                      </div>
                      <div className="ml-4 w-16 text-center" data-id="element-13703">
                        <input type="number" min="0" max="100" value={thresholds[method.id]} onChange={e => handleThresholdChange(method.id, parseInt(e.target.value))} className="w-14 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-13704" />
                      </div>
                    </div>
                    <div className="flex justify-between mt-3" data-id="element-13705">
                      <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-13706">
                        Revenue: ${method.revenue.toLocaleString()}
                      </div>
                      <div className="flex items-center gap-4" data-id="element-13707">
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-13708">
                          <Bot className="w-3 h-3 text-indigo-500 mr-1" data-id="element-13709" />
                          <span data-id="element-13710">${method.aiRevenue.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-13711">
                          <User className="w-3 h-3 text-green-500 mr-1" data-id="element-13712" />
                          <span data-id="element-13713">${method.humanRevenue.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-13714">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13715">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13716">
                <BarChart size={18} className="text-indigo-500 mr-2" data-id="element-13717" />
                Segment Fallback Configuration
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-13718">
              <table className="w-full" data-id="element-13719">
                <thead data-id="element-13720">
                  <tr className="bg-gray-50 dark:bg-gray-900/30" data-id="element-13721">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-13722">
                      Segment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-13723">
                      Algorithm Confidence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-13724">
                      Fallback Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-13725">
                      Retention Risk
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-13726">
                  {segments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60" data-id="element-13727">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-13728">
                        <div className="flex items-center" data-id="element-13729">
                          <div className="flex-shrink-0" data-id="element-13730">
                            <div className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center" data-id="element-13731">
                              {segment.icon}
                            </div>
                          </div>
                          <div className="ml-3" data-id="element-13732">
                            <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13733">
                              {segment.label}
                              <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full" data-id="element-13734">
                                {segment.customerClass}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-13735">
                        <div className="flex items-center" data-id="element-13736">
                          <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-13737">
                            <div className="h-full bg-purple-500 rounded-full" style={{
                          width: `${segment.algorithmConfidence}%`
                        }} data-id="element-13738"></div>
                          </div>
                          <span className="ml-2" data-id="element-13739">
                            {segment.algorithmConfidence}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-13740">
                        <select value={segmentFallbacks[segment.id]} onChange={e => handleFallbackMethodChange(segment.id, e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 dark:focus:border-purple-400" data-id="element-13741">
                          {fallbackMethods.map(method => <option key={method.id} value={method.id} data-id="element-13742">
                              {method.label}
                            </option>)}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-13743">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${segment.retentionRisk === 'low' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : segment.retentionRisk === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' : segment.retentionRisk === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`} data-id="element-13744">
                          {segment.retentionRisk.charAt(0).toUpperCase() + segment.retentionRisk.slice(1)}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div data-id="element-13745">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-13746">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13747">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13748">
                <DollarSign size={18} className="text-green-500 mr-2" data-id="element-13749" />
                AI Commission Settings
              </h3>
            </div>
            <div className="p-4" data-id="element-13750">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-13751">
                Configure the commission rates for AI-generated revenue across
                different tiers.
              </p>
              <div className="space-y-4" data-id="element-13752">
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4" data-id="element-13753">
                  <div className="flex items-center justify-between mb-2" data-id="element-13754">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13755">
                      Free Tier
                    </h4>
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-13756">
                      <span className="font-medium" data-id="element-13757">
                        {aiCommissionRates.free}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-13758">
                    <input type="range" min="5" max="30" step="0.5" value={aiCommissionRates.free} onChange={e => handleCommissionRateChange('free', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-13759" />
                    <input type="number" min="5" max="30" step="0.5" value={aiCommissionRates.free} onChange={e => handleCommissionRateChange('free', parseFloat(e.target.value))} className="ml-4 w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-13760" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-13761">
                    Applied to all AI revenue for users with $0 lifetime spend
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4" data-id="element-13762">
                  <div className="flex items-center justify-between mb-2" data-id="element-13763">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13764">
                      Standard Tier ($30+)
                    </h4>
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-13765">
                      <span className="font-medium" data-id="element-13766">
                        {aiCommissionRates.standard}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-13767">
                    <input type="range" min="5" max="20" step="0.5" value={aiCommissionRates.standard} onChange={e => handleCommissionRateChange('standard', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-13768" />
                    <input type="number" min="5" max="20" step="0.5" value={aiCommissionRates.standard} onChange={e => handleCommissionRateChange('standard', parseFloat(e.target.value))} className="ml-4 w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-13769" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-13770">
                    Applied to all AI revenue for users with $30+ lifetime spend
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4" data-id="element-13771">
                  <div className="flex items-center justify-between mb-2" data-id="element-13772">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13773">
                      Premium Tier ($100+)
                    </h4>
                    <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-13774">
                      <span className="font-medium" data-id="element-13775">
                        {aiCommissionRates.premium}%
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-13776">
                    <input type="range" min="5" max="15" step="0.5" value={aiCommissionRates.premium} onChange={e => handleCommissionRateChange('premium', parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" data-id="element-13777" />
                    <input type="number" min="5" max="15" step="0.5" value={aiCommissionRates.premium} onChange={e => handleCommissionRateChange('premium', parseFloat(e.target.value))} className="ml-4 w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-13778" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-13779">
                    Applied to all AI revenue for users with $100+ lifetime
                    spend
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-13780">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13781">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13782">
                <Repeat size={18} className="text-indigo-500 mr-2" data-id="element-13783" />
                Fallback Priority Order
              </h3>
            </div>
            <div className="p-4" data-id="element-13784">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-13785">
                Drag to reorder the fallback methods. The system will try each
                method in order when algorithm confidence is below threshold.
              </p>
              <div className="space-y-2" data-id="element-13786">
                {fallbackPriority.map((methodId, index) => {
                const method = fallbackMethods.find(m => m.id === methodId)!;
                return <div key={methodId} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/20 rounded-lg p-3" data-id="element-13787">
                      <div className="flex items-center" data-id="element-13788">
                        <div className="w-6 h-6 flex items-center justify-center mr-2 text-gray-500 dark:text-gray-400" data-id="element-13789">
                          {index + 1}
                        </div>
                        <div className="w-8 h-8 rounded-md bg-white dark:bg-gray-800 flex items-center justify-center mr-3" data-id="element-13790">
                          {method.icon}
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13791">
                          {method.label}
                        </div>
                      </div>
                      <div className="flex items-center" data-id="element-13792">
                        <button onClick={() => handleMoveUp(index)} disabled={index === 0} className={`p-1 rounded-md ${index === 0 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-13793">
                          ↑
                        </button>
                        <button onClick={() => handleMoveDown(index)} disabled={index === fallbackPriority.length - 1} className={`p-1 rounded-md ${index === fallbackPriority.length - 1 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-13794">
                          ↓
                        </button>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-13795">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13796">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13797">
                <Brain size={18} className="text-purple-500 mr-2" data-id="element-13798" />
                AI Coaching Suggestions
              </h3>
            </div>
            <div className="p-4" data-id="element-13799">
              <div className="space-y-4" data-id="element-13800">
                {aiCoachingSuggestions.map(suggestion => <div key={suggestion.id} className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4" data-id="element-13801">
                    <div className="flex items-start" data-id="element-13802">
                      <div className="flex-shrink-0 mt-1" data-id="element-13803">
                        <Brain size={16} className="text-purple-500" data-id="element-13804" />
                      </div>
                      <div className="ml-3" data-id="element-13805">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13806">
                          {suggestion.title}
                        </h4>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-13807">
                          {suggestion.description}
                        </p>
                        <div className="mt-2 flex items-center" data-id="element-13808">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-13809">
                            Impact:
                          </span>
                          <span className={`ml-1 text-xs font-medium ${suggestion.impact === 'high' ? 'text-green-600 dark:text-green-400' : suggestion.impact === 'medium' ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} data-id="element-13810">
                            {suggestion.impact.charAt(0).toUpperCase() + suggestion.impact.slice(1)}
                          </span>
                          <span className="mx-2 text-gray-300 dark:text-gray-600" data-id="element-13811">
                            |
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-13812">
                            Segment:
                          </span>
                          <span className="ml-1 text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-13813">
                            {suggestion.segment === 'all' ? 'All Segments' : segments.find(s => s.id === suggestion.segment)?.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-13814">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13815">
              <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13816">
                <AlertTriangle size={18} className="text-amber-500 mr-2" data-id="element-13817" />
                Risk Alerts
              </h3>
            </div>
            <div className="p-4" data-id="element-13818">
              <div className="space-y-4" data-id="element-13819">
                {riskAlerts.map(alert => <div key={alert.id} className={`${alert.severity === 'high' ? 'bg-red-50 dark:bg-red-900/20' : alert.severity === 'medium' ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-blue-50 dark:bg-blue-900/20'} rounded-lg p-4`} data-id="element-13820">
                    <div className="flex items-start" data-id="element-13821">
                      <div className="flex-shrink-0 mt-1" data-id="element-13822">
                        <AlertTriangle size={16} className={alert.severity === 'high' ? 'text-red-500' : alert.severity === 'medium' ? 'text-amber-500' : 'text-blue-500'} data-id="element-13823" />
                      </div>
                      <div className="ml-3" data-id="element-13824">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-13825">
                          {alert.title}
                        </h4>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-13826">
                          {alert.description}
                        </p>
                        <div className="mt-2 flex items-center" data-id="element-13827">
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-13828">
                            Severity:
                          </span>
                          <span className={`ml-1 text-xs font-medium ${alert.severity === 'high' ? 'text-red-600 dark:text-red-400' : alert.severity === 'medium' ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} data-id="element-13829">
                            {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                          </span>
                          <span className="mx-2 text-gray-300 dark:text-gray-600" data-id="element-13830">
                            |
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-13831">
                            Segment:
                          </span>
                          <span className="ml-1 text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-13832">
                            {alert.segment === 'all' ? 'All Segments' : segments.find(s => s.id === alert.segment)?.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-13833">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13834">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13835">
            <Info size={18} className="text-blue-500 mr-2" data-id="element-13836" />
            Export Settings
          </h3>
        </div>
        <div className="p-6" data-id="element-13837">
          <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4" data-id="element-13838">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2" data-id="element-13839">
              Include in Exports:
            </h4>
            <div className="space-y-2" data-id="element-13840">
              <label className="flex items-center" data-id="element-13841">
                <input type="checkbox" checked={true} readOnly className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" data-id="element-13842" />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-13843">
                  AI vs Human Revenue Split
                </span>
              </label>
              <label className="flex items-center" data-id="element-13844">
                <input type="checkbox" checked={true} readOnly className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" data-id="element-13845" />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-13846">
                  AI Commission Fee
                </span>
              </label>
              <label className="flex items-center" data-id="element-13847">
                <input type="checkbox" checked={true} readOnly className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" data-id="element-13848" />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-13849">
                  Customer Classification
                </span>
              </label>
              <label className="flex items-center" data-id="element-13850">
                <input type="checkbox" checked={true} readOnly className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" data-id="element-13851" />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-13852">
                  Hybrid Routing Data
                </span>
              </label>
            </div>
            <button onClick={handleExportData} className="mt-3 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium" data-id="element-13853">
              Export All Settings
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6" data-id="element-13854">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50" data-id="element-13855">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center" data-id="element-13856">
            <Info size={18} className="text-blue-500 mr-2" data-id="element-13857" />
            Monetization Mantra
          </h3>
        </div>
        <div className="p-6 text-center" data-id="element-13858">
          <p className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-13859">
            "Every spender, every click, every fallback = monetized."
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-13860">
            The algorithm attempts to provide the most personalized experience
            possible. When it can't, our fallback system ensures we still
            deliver targeted monetization opportunities.
          </p>
        </div>
      </div>
    </div>;
};